/** @format */

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map } from 'rxjs/operators';

import { BaseState, IState } from '@shared/data-access/interfaces';
import { Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

// Default state
const defaultState: BaseState = {
  AUTHENTICATED: false,
  CART_COUNT: 0,
};

export class Store<TState extends IState = IState> {
  private readonly subject = new BehaviorSubject<TState>(defaultState as TState);

  private readonly store = this.subject.asObservable().pipe(distinctUntilChanged());

  public get value() {
    return this.subject.value;
  }

  public select<K extends keyof TState>(name: K): Observable<TState[K]> {
    return this.store.pipe(map((state) => state[name]));
  }

  public selectDefined<K extends keyof TState>(name: K): Observable<NonNullable<TState[K]>> {
    return this.select(name).pipe(
      filter((result) => {
        return result !== undefined && result !== null;
      }),
    );
  }

  public set<K extends keyof TState>(name: K, state: TState[K]): void {
    this.subject.next({
      ...this.value,
      [name]: state,
    });
  }

  public selectDistinctKey<K extends keyof TState>(name: K): Observable<TState[K]> {
    return this.store.pipe(
      distinctUntilKeyChanged(name),
      map((state) => state[name]),
    );
  }

  public selectDistinctDefinedKey<K extends keyof TState>(
    name: K,
  ): Observable<NonNullable<TState[K]>> {
    return this.selectDistinctKey(name).pipe(
      filter((result) => {
        return result !== undefined && result !== null;
      }),
    );
  }

  /**
   * @describe Signal based selectDistinctKey.
   * Must be used in an injection context (constructor, field initializer, or within runInInjectionContext).
   * Always check for falsy values as toSignal defaults to undefined until a value is emitted.
   */
  public selectDistinctKeySignal<K extends keyof TState>(name: K): Signal<TState[K] | undefined> {
    return toSignal(this.selectDistinctKey(name));
  }

  /**
   * @describe Signal based selectDistinctDefinedKey.
   * Must be used in an injection context (constructor, field initializer, or within runInInjectionContext).
   * Always check for falsy values as toSignal defaults to undefined until a value is emitted.
   */
  public selectDistinctDefinedKeySignal<K extends keyof TState>(
    name: K,
  ): Signal<NonNullable<TState[K]> | undefined> {
    return toSignal(this.selectDistinctDefinedKey(name));
  }
}
